import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import CommandLayout from "../../components/war-room/CommandLayout";
import CorpusSearch from "../../components/war-room/CorpusSearch";
import ThreeWayReview, { reviewColor, type ReviewOption } from "../../components/war-room/ThreeWayReview";
import { useCaseAuthority, useSetAuthorityAssessment, type AuthorityClaim, type AuthorityElement } from "../../hooks/useCaseAuthority";

/**
 * Layer 3 — Authority ledger. Per claim -> element: was the act ALLOWED by a
 * governing instrument? The engine renders a verdict (authorized / unauthorized /
 * unclear / n-a) + stage (facial 12(b)(6) vs contested for SJ); the human makes
 * one 3-way call — Allowed / Partly Allowed / Not Allowed. Detail (cited
 * instruments, the two-model read, corpus search, note) lives behind "details".
 */
const AUTHORITY_OPTIONS: ReviewOption[] = [
  { key: "allowed", label: "Allowed", color: "#5a8f6b", mark: "✓" },
  { key: "partial", label: "Partly Allowed", color: "#c9a23a", mark: "◐" },
  { key: "not_allowed", label: "Not Allowed", color: "#b3503f", mark: "✕" },
];
const UNREVIEWED = "#a78bdb";

function AuthorityLedger() {
  const { caseId } = useParams<{ caseId: string }>();
  const { data: claims, isLoading, error } = useCaseAuthority(caseId);

  let total = 0, contested = 0, facial = 0, reviewed = 0, pending = 0;
  for (const c of claims ?? []) for (const el of c.elements) {
    total++;
    if (el.verdict === "unclear") contested++;
    if (el.verdict === "unauthorized") facial++;
    if (el.human_assessment) reviewed++;
    if (el.needs_review) pending++;
  }

  return (
    <CommandLayout caseId={caseId} variant="ctafinal">
      <header className="rounded border border-steel-dark bg-iron/70 px-6 py-6 backdrop-blur-sm">
        <p className="mb-2 font-display text-base font-light uppercase tracking-[0.18em] text-bone-mute">// layer 3 — authority</p>
        <h2 className="font-display text-2xl font-light leading-tight tracking-tight text-bone md:text-3xl"
            style={{ textShadow: "0 2px 16px rgba(0,0,0,0.6)" }}>
          Was the act allowed by the instrument?
        </h2>
        <p className="mt-3 max-w-3xl font-display text-sm leading-relaxed text-bone-dim">
          For each conduct element, the engine asks whether a governing instrument authorized it:
          <span style={{ color: vColor("unclear") }}> contested</span> (a real but disputed authorization pathway — the
          summary-judgment battleground), <span style={{ color: vColor("unauthorized") }}> facially unauthorized</span> (no
          instrument reaches it), or <span style={{ color: vColor("authorized") }}> authorized</span>. You make the call:
          <span style={{ color: reviewColor("allowed", AUTHORITY_OPTIONS) }}> Allowed</span> /
          <span style={{ color: reviewColor("partial", AUTHORITY_OPTIONS) }}> Partly</span> /
          <span style={{ color: reviewColor("not_allowed", AUTHORITY_OPTIONS) }}> Not Allowed</span>.
        </p>
        <p className="mt-2 max-w-3xl font-display text-sm leading-relaxed text-bone-mute/80">
          Cross-layer gate: conduct your accuracy review (L1) ruled false is pulled from the engine&apos;s view and flagged
          <span style={{ color: "#6b7480" }}> L1: contradicted</span> (greyed out below) — those elements die at L1, so authority is moot.
        </p>

        {(claims?.length ?? 0) > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-3 font-display text-base font-light uppercase tracking-[0.1em] text-bone-mute">
            <Tally label="contested · SJ" n={contested} color={vColor("unclear")} />
            <Tally label="facially unauthorized" n={facial} color={vColor("unauthorized")} />
            <span>· {reviewed}/{total} reviewed</span>
            <span className="flex items-center gap-1.5"><i className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: UNREVIEWED }} />{pending} to review</span>
            <GradingGuide />
            <Link to={`/case/${caseId}/relevance`} className="ml-auto normal-case tracking-normal text-blue-bright/80 hover:text-blue-bright">← relevance</Link>
            <Link to={`/case/${caseId}/justification`} className="normal-case tracking-normal text-blue-bright/80 hover:text-blue-bright">→ justification</Link>
          </div>
        )}
      </header>

      <div className="space-y-3">
        {isLoading && <p className="font-display text-base text-bone-mute">// reading the instruments…</p>}
        {error && <p className="font-display text-base text-ember-bright">// {error.message}</p>}
        {!isLoading && !error && (claims?.length ?? 0) === 0 && (
          <p className="rounded border border-steel-dark bg-iron/70 px-5 py-6 font-display text-base text-bone-mute">
            // no authority map yet — run the Layer 3 engine (authority_runner.py) first
          </p>
        )}
        {(claims ?? []).map((c) => <ClaimCard key={c.claim_id} c={c} />)}
      </div>
    </CommandLayout>
  );
}

const VC: Record<string, string> = { authorized: "#5a8f6b", unauthorized: "#b3503f", unclear: "#c9a23a", not_applicable: "#6b7480", error: "#6b7480" };
const vColor = (k: string | null | undefined): string => (k && VC[k]) || "#5a6470";
const VL: Record<string, string> = { authorized: "authorized", unauthorized: "facially unauthorized", unclear: "contested", not_applicable: "n/a", error: "error" };
const vLabel = (k: string | null | undefined): string => (k && (VL[k] ?? k)) || "—";
const STAGE_L: Record<string, string> = { facial_12b6: "facial · 12(b)(6)", merits_sj: "contested · summary judgment", na: "" };
const GUIDE: Record<string, string> = {
  authorized: "An instrument's grantee + scope + validity authorize this act on the instrument's face.",
  unclear: "A real but conditional/contested authorization pathway whose trigger is a disputed fact — fought at summary judgment, not on the pleadings.",
  unauthorized: "No instrument reaches the act at all — authority won't save it (win it on truth or justification instead).",
  not_applicable: "Not an authorization question (duty, causation, damages, relief).",
};

function Tally({ label, n, color }: { label: string; n: number; color: string }) {
  return <span className="flex items-center gap-1.5"><i className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: color }} />{n} {label}</span>;
}

function GradingGuide() {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative normal-case tracking-normal">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 rounded border border-steel-dark/70 px-2 py-0.5 text-bone-mute hover:text-bone">ⓘ what the verdicts mean</button>
      {open && (
        <div className="absolute left-0 z-20 mt-1 w-[24rem] rounded border border-steel-dark bg-charcoal/95 p-3 shadow-lg backdrop-blur-sm">
          {(["authorized", "unclear", "unauthorized", "not_applicable"] as const).map((k) => (
            <div key={k} className="mb-2 flex items-start gap-2">
              <span className="mt-0.5 shrink-0 rounded px-2 py-0.5 font-display text-sm uppercase tracking-[0.1em]" style={{ background: vColor(k), color: "#0c0f12" }}>{vLabel(k)}</span>
              <span className="font-display text-[12px] leading-snug text-bone-dim">{GUIDE[k]}</span>
            </div>
          ))}
          <p className="font-display text-[11px] italic text-bone-mute/50">Analytical guidance — your counsel makes the final call.</p>
        </div>
      )}
    </span>
  );
}

function ClaimCard({ c }: { c: AuthorityClaim }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden rounded border border-steel-dark bg-iron/70 backdrop-blur-sm">
      <button type="button" onClick={() => setOpen((o) => !o)} className="block w-full text-left hover:bg-iron-2/40">
        <div className="flex items-start gap-3 px-5 py-3">
          <div className="min-w-0 flex-1">
            <p className="font-display text-base font-light text-bone">{c.claim_name}</p>
            {c.target && <p className="mt-0.5 font-display text-sm text-bone-mute/70">vs {c.target}</p>}
          </div>
          <span className="shrink-0 rounded px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.1em]"
                style={{ background: c.unclear > 0 ? vColor("unclear") : (c.unauthorized > 0 ? vColor("unauthorized") : "#5a8f6b"), color: "#0c0f12" }}>
            {c.unclear} contested · {c.unauthorized} facial{c.l1_contradicted > 0 ? ` · ${c.l1_contradicted} L1-false` : ""} · {c.reviewed}/{c.total} reviewed
          </span>
          <span className="mt-0.5 shrink-0 font-display text-base text-bone-mute">{open ? "▾" : "▸"}</span>
        </div>
      </button>
      {open && (
        <div className="border-t border-steel-dark/60 px-5 py-3 space-y-2">
          {c.elements.map((el) => <ElementRow key={el.id} el={el} />)}
        </div>
      )}
    </div>
  );
}

function ElementRow({ el }: { el: AuthorityElement }) {
  const { caseId } = useParams<{ caseId: string }>();
  const set = useSetAuthorityAssessment(caseId);
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(el.human_note ?? "");
  const split = el.review_status === "split";
  const dead = el.l1_status === "contradicted";
  const stage = el.stage && STAGE_L[el.stage] ? STAGE_L[el.stage] : "";
  const border = dead ? "#5a6470" : (el.human_assessment ? reviewColor(el.human_assessment, AUTHORITY_OPTIONS) : (el.needs_review ? UNREVIEWED : vColor(el.verdict)));
  const choose = (v: string) => set.mutate({ id: el.id, assessment: v, note: note.trim() || undefined });

  return (
    <div className={`rounded border border-steel-dark/60 bg-black/20 ${dead ? "opacity-60" : ""}`}>
      <div className="border-l-2 px-3 py-2" style={{ borderColor: border }}>
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-start gap-2 text-left">
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="rounded px-2 py-0.5 font-display text-[11px] font-light uppercase tracking-[0.1em]" style={{ background: dead ? "#3a4048" : vColor(el.verdict), color: dead ? "#9aa3ad" : "#0c0f12" }}>{vLabel(el.verdict)}</span>
              {el.l1_status === "contradicted" && (
                <span className="rounded px-2 py-0.5 font-display text-[11px] font-light uppercase tracking-[0.1em]" style={{ background: "#5a6470", color: "#e8e2d6" }}>✓ L1: contradicted · dead</span>
              )}
              {el.l1_status === "contested" && (
                <span className="rounded border px-2 py-0.5 font-display text-[11px] font-light uppercase tracking-[0.1em]" style={{ borderColor: "#c9a23a", color: "#c9a23a" }}>L1: contested</span>
              )}
              {stage && <span className="font-display text-[11px] font-light uppercase tracking-[0.08em] text-bone-mute/70">{stage}</span>}
              {split && <span className="rounded px-1.5 py-0.5 font-display text-[11px] uppercase tracking-[0.1em]" style={{ background: UNREVIEWED, color: "#0c0f12" }}>split</span>}
              {el.needs_review && <span className="font-display text-[11px] uppercase tracking-[0.1em]" style={{ color: UNREVIEWED }}>· needs your call</span>}
            </div>
            <p className="font-display text-[13px] text-bone">{el.element_name}</p>
          </div>
          <span className="mt-0.5 shrink-0 font-display text-sm text-bone-mute">{open ? "▾ details" : "▸ details"}</span>
        </button>

        <div className="mt-2.5">
          <ThreeWayReview value={el.human_assessment} options={AUTHORITY_OPTIONS} disabled={set.isPending} onChange={choose} />
          {set.isError && <p className="mt-1 font-display text-sm text-ember-bright">// {(set.error as Error).message}</p>}
        </div>
      </div>

      {open && (
        <div className="border-l-2 px-3 pb-3" style={{ borderColor: border }}>
          {el.rationale && <p className="mt-2 font-display text-[12px] leading-snug text-bone-dim">{el.rationale}</p>}

          {(el.cited?.length ?? 0) > 0 && (
            <div className="mt-2">
              <p className="mb-1 font-display text-[11px] font-light uppercase tracking-[0.12em] text-bone-mute/70">// instruments relied on</p>
              <div className="flex flex-wrap gap-1.5">
                {(el.cited ?? []).map((a) => (
                  <span key={a.authority_id} className="rounded border border-steel-dark/70 px-2 py-0.5 font-display text-[11px] text-bone-dim">{a.title}</span>
                ))}
              </div>
            </div>
          )}

          {(el.proposer_verdict || el.judge_verdict) && (
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 font-display text-[11px] font-light uppercase tracking-[0.08em] text-bone-mute/70">
              <span className="flex items-center gap-1">proposer<span className="rounded px-1 py-0.5" style={{ background: vColor(el.proposer_verdict), color: "#0c0f12" }}>{vLabel(el.proposer_verdict)}</span></span>
              <span className="flex items-center gap-1">cross-exam<span className="rounded px-1 py-0.5" style={{ background: vColor(el.judge_verdict), color: "#0c0f12" }}>{vLabel(el.judge_verdict)}</span></span>
              {split && <span className="text-bone-mute/60">— they disagreed; the cross-examiner's call stands</span>}
            </div>
          )}

          <textarea value={note} onChange={(e) => setNote(e.target.value)}
            onBlur={() => { if ((note.trim() || null) !== (el.human_note ?? null) && el.human_assessment) choose(el.human_assessment); }} rows={1}
            placeholder="note — why (saved with your call)"
            className="mt-2 w-full resize-y rounded border border-steel-dark/60 bg-black/30 px-2 py-1 font-display text-[12px] text-bone placeholder:text-bone-mute/50" />

          <CorpusSearch seed={el.element_name ?? ""} label="⌕ search the corpus for this" />
        </div>
      )}
    </div>
  );
}

export default AuthorityLedger;
